import { useMutation } from '@apollo/client';
import { useContext } from 'react';
import { useReturnOrderFromInvoiceMutation } from '../../utils/SalesOrderHooks';
import ModalContext from '../../components/Context/ModalContext';
import SalesInvoiceContext from '../../components/Context/SalesInvoiceContext';
import ConnectedFormContext from '../../components/Context/ConnectedFormContext';
import { useTemporarySave } from '../../components/connectedForm/utils/handleTemporarySave';
import SalesCreditAndRefundFromInvoiceLinesMutation from '../../graphql/mutations/salesLedger/SalesCreditAndRefundFromInvoiceLines.graphql';
import { useComponentQueryErrorHandling } from '../../utils/parseErrorResponse';
import getClientMutationId from '../../utils/getClientMutationId';
import { serviceActions } from '../../state/modules/service';
import normalizeVariables, { normalizeFields } from '../../graphql/schema/normalizeVariables';
import { useActionProps } from '../../utils/useActionProps';
import { SalesInvoiceFormAction } from './salesInvoice/SalesInvoiceAction';
import { SalesCreditMiniForm } from '../../views/salesLedger/salesCredit/salesCreditCommon';
import { useDispatch } from 'react-redux';
import { ISalesInvoiceLine } from '../../graphql/schema/p4types';

export const useGenerateCreditFromInvoiceLinesAction = () => {
  const dispatch = useDispatch();
  const salesInvoiceContext = useContext(SalesInvoiceContext);
  const { onClose } = useContext(ModalContext);
  const actionProps = useActionProps<{ originLines: Array<ISalesInvoiceLine> }>('originLines');
  const { raiseReturn } = useReturnOrderFromInvoiceMutation();

  const handleQueryError = useComponentQueryErrorHandling();
  const [creditAndReturnMutation] = useMutation(SalesCreditAndRefundFromInvoiceLinesMutation, {
    onError: handleQueryError,
  });

  const connectedForm = useContext(ConnectedFormContext);
  const temporarySave = useTemporarySave();

  if (!salesInvoiceContext.setReturnOrderModalState) {
    return null;
  }

  return [
    {
      key: 'raiseReturn',
      text: 'Raise Return',
      onClick: () => {
        raiseReturn(actionProps);
        onClose?.(); //Close the line workspace modal (This action is only displayed when in a modal from the sales order form.
        connectedForm.reloadData?.(true)?.then(() => {
          //This isn't ideal but what wants to happen here is for the form data to refresh & include updated item information (e.g. The returned amount on the lines).
          //The reloadData refreshes the form, but item information is only regenerated on a temporary save.
          //The other way would be a new endpoint (Or alter the existing find to support this) to do it in one call, but I don't think that's necessary for now.
          temporarySave();
        });
      },
      actionReference: SalesInvoiceFormAction.ReturnOrder,
    },
    {
      key: 'creditAndRefund',
      text: 'Credit & refund',
      onClick: () => {
        if (actionProps.include && (!actionProps.originLines || actionProps.originLines.length === 0)) {
          dispatch(serviceActions.genericAlert('Invalid action', 'Must select one or more lines.'));
        }

        creditAndReturnMutation({
          variables: {
            input: {
              clientMutationId: getClientMutationId(),
              ...normalizeVariables(actionProps, [...normalizeFields, 'lineCost', 'haggleLineCost']),
            },
          },
        }).then((data) => {
          const salesCredit = data?.data?.salesCreditAndRefundFromInvoiceLines?.result;
          if (salesCredit?.uuid) {
            salesInvoiceContext.setReturnOrderModalState({
              modalOpen: true,
              uuid: salesCredit.uuid,
              initialValues: salesCredit,
              miniForm: SalesCreditMiniForm.Refund,
            });
          }
        });
        onClose?.(); //Close the line workspace modal (This action is only displayed when in a modal from the sales order form.
        connectedForm.reloadData?.(true)?.then(() => {
          //This isn't ideal but what wants to happen here is for the form data to refresh & include updated item information (e.g. The returned amount on the lines).
          //The reloadData refreshes the form, but item information is only regenerated on a temporary save.
          //The other way would be a new endpoint (Or alter the existing find to support this) to do it in one call, but I don't think that's necessary for now.
          temporarySave();
        });
      },
      actionReference: SalesInvoiceFormAction.CreditRefundInvoice,
    },
  ];
};
